/**
 * H16 ดันเล็ก (data/smallDungeons.ts): the zone's open camp is set down when the World is made. When every camp monster
 * is dead and the player was inside the area and hit one of them, the camp's daily reward is paid once per game day
 * (`progress.smallDungeonDay`); the camp respawns after its timer. The game day counts clock wraps (`setDay` lets the
 * client or server pin it to the calendar). Notices go to the HUD through `host.notice`.
 */
import { randomFloorIn } from '../map';
import { smallDungeonsIn, type SmallDungeon } from '../data/smallDungeons';
import type { Entity, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

interface CampState {
  readonly def: SmallDungeon;
  ids: number[];
  /** Player entity ids that hit a camp monster while inside the area. */
  hit: Set<number>;
  /** Seconds since the clear; null while the camp stands. */
  clearT: number | null;
}

export class SmallDungeonsSystem implements WorldSystem {
  readonly name = 'smallDungeons';
  private camps: CampState[] = [];
  private day = 0;
  /** Pinned to the real calendar (dayReset.ts): the clock-wrap count no longer moves it. */
  private pinned = false;
  private lastHour = -1;

  constructor(private readonly h: SystemHost) {}

  /** Pins the game day (a calendar day number); claims compare against it. */
  realDay(day: number): void {
    this.setDay(day);
  }
  setDay(day: number): void {
    this.day = day;
    this.pinned = true;
  }
  get gameDay(): number {
    return this.day;
  }
  /** Rewards paid to players other than the local one (a server pays them from its own store). */
  readonly payouts: { playerId: number; campId: string; day: number }[] = [];
  private readonly claims = new Map<string, number>();

  /** The camps of this zone and whether each stands. */
  state(): readonly { id: string; name: string; x: number; y: number; radius: number; alive: number; cleared: boolean; claimed: boolean }[] {
    return this.camps.map((c) => ({
      id: c.def.id,
      name: c.def.name,
      x: c.def.at.x,
      y: c.def.at.y,
      radius: c.def.radius,
      alive: this.alive(c).length,
      cleared: c.clearT !== null,
      claimed: this.h.world.progress.smallDungeonDay?.[c.def.id] === this.day,
    }));
  }

  setup(): void {
    const w = this.h.world;
    if (w.map.interior) return;
    for (const def of smallDungeonsIn(w.map.zone)) {
      const c: CampState = { def, ids: [], hit: new Set(), clearT: null };
      this.camps.push(c);
      this.spawn(c);
    }
  }

  private spawn(c: CampState): void {
    const { def } = c;
    const m = this.h.world.map;
    const box: [number, number, number, number] = [
      Math.max(1, Math.floor(def.at.x - def.radius)),
      Math.min(m.w - 1, Math.ceil(def.at.x + def.radius)),
      Math.max(1, Math.floor(def.at.y - def.radius)),
      Math.min(m.h - 1, Math.ceil(def.at.y + def.radius)),
    ];
    const roster = [def.boss, def.sub, ...Array.from({ length: def.minions }, () => def.minion)];
    c.ids = roster.map((id) => {
      const p = randomFloorIn(m, box, () => this.h.random());
      return this.h.spawnMonster(id, p.x, p.y).id;
    });
    c.hit.clear();
    c.clearT = null;
  }

  private alive(c: CampState): Entity[] {
    const es = this.h.world.entities;
    return c.ids.map((id) => es.get(id)).filter((e): e is Entity => !!e && !e.dead);
  }

  private inside(c: CampState, e: Entity): boolean {
    return Math.hypot(e.x - c.def.at.x, e.y - c.def.at.y) <= c.def.radius + 4;
  }

  observe(e: WorldEvent): void {
    if (e.type !== 'hit' || !this.camps.length) return;
    const w = this.h.world;
    const src = w.entities.get(e.srcId);
    if (!src || src.kind !== 'player') return;
    for (const c of this.camps) if (c.ids.includes(e.tgtId) && this.inside(c, src)) c.hit.add(src.id);
  }

  step(dt: number): void {
    const w = this.h.world;
    if (this.lastHour >= 0 && w.hour < this.lastHour && !this.pinned) this.day++;
    this.lastHour = w.hour;
    for (const c of this.camps) {
      if (c.clearT !== null) {
        c.clearT += dt;
        if (c.clearT >= c.def.respawn) {
          for (const id of c.ids) w.entities.delete(id);
          this.spawn(c);
          this.h.notice(`${c.def.name} กลับมามีผู้บุกยึดอีกครั้ง`);
        }
        continue;
      }
      if (this.alive(c).length) continue;
      c.clearT = 0;
      const pr = w.progress;
      for (const pid of [...c.hit].sort((x, y) => x - y)) {
        if (pid === w.player.id) {
          if (pr.smallDungeonDay?.[c.def.id] === this.day) {
            this.h.notice(`กวาดล้าง${c.def.name}แล้ว (รางวัลประจำวันรับไปแล้ว)`);
            continue;
          }
          (pr.smallDungeonDay ??= {})[c.def.id] = this.day;
          const text = this.h.grantReward(c.def.reward);
          this.h.notice(`กวาดล้าง${c.def.name}สำเร็จ รางวัลประจำวัน: ${text}`);
        } else {
          const k = `${pid}|${c.def.id}`;
          if (this.claims.get(k) === this.day) continue;
          this.claims.set(k, this.day);
          this.payouts.push({ playerId: pid, campId: c.def.id, day: this.day });
        }
      }
    }
  }
}

type Host = { sys: { smallDungeons: SmallDungeonsSystem } };
/** Pins the game day of a World's camps to the calendar (client or server). */
export const setSmallDungeonDay = (world: object, day: number): void => (world as unknown as Host).sys.smallDungeons.setDay(day);
/** The camps of the World's zone: position, name, alive count, cleared (waiting to respawn), claimed today. */
export const smallDungeonState = (world: object): ReturnType<SmallDungeonsSystem['state']> =>
  (world as unknown as Host).sys.smallDungeons.state();
